import type { Ref } from 'vue'
import { TOKEN_CONSTANTS } from './tokenUtils'
import { showPaymentTimeExpiredAlert } from './modalUtils'
import type { TokenExpirationInfo } from '@entities/order'

/**
 * Класс для управления таймером истечения токена
 */
export class ExpirationTimer {
  private timer: ReturnType<typeof setInterval> | null = null
  private isRunning = false

  /**
   * Запускает таймер для отслеживания истечения токена
   * @param tokenExpiration - реактивная ссылка на информацию об истечении токена
   * @param hasUnpaidOrder - реактивная ссылка на флаг наличия неоплаченного заказа
   * @param onExpired - колбэк при истечении токена
   */
  start(
    tokenExpiration: Ref<TokenExpirationInfo | null>,
    hasUnpaidOrder: Ref<boolean>,
    onExpired: () => void,
    onTimeUpdate?: (time: number) => void, // Добавляем колбэк для обновления времени
  ): void {
    if (this.isRunning) {
      return
    }

    this.isRunning = true

    this.timer = setInterval(() => {
      
      if (tokenExpiration.value) {
        // Обновляем время истечения на основе текущего времени
        const now = new Date()
        const created = new Date(tokenExpiration.value.createdAt)
        const expires = new Date(created.getTime() + TOKEN_CONSTANTS.LIFETIME_MS)
        const remainingTime = expires.getTime() - now.getTime()
        
        // Вызываем колбэк для обновления времени, если он предоставлен
        if (onTimeUpdate) {
          onTimeUpdate(Math.max(0, remainingTime))
        }
        
        // Обновляем isExpired в tokenExpiration
        tokenExpiration.value = {
          ...tokenExpiration.value,
          isExpired: remainingTime <= 0,
        }

        // Если токен истек, останавливаем таймер и выполняем колбэк
        if (remainingTime <= 0) {
          this.stop()
          
          if (hasUnpaidOrder.value) {
            showPaymentTimeExpiredAlert()
            onExpired()
          }
        }
      }
    }, TOKEN_CONSTANTS.UPDATE_INTERVAL_MS)

  }

  /**
   * Останавливает таймер
   */
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
      this.isRunning = false
    }
  }

  /**
   * Проверяет, запущен ли таймер
   * @returns true если таймер запущен
   */
  isActive(): boolean {
    return this.isRunning
  }

  /**
   * Перезапускает таймер
   * @param tokenExpiration - реактивная ссылка на информацию об истечении токена
   * @param hasUnpaidOrder - реактивная ссылка на флаг наличия неоплаченного заказа
   * @param onExpired - колбэк при истечении токена
   * @param onTimeUpdate - колбэк для обновления времени
   */
  restart(
    tokenExpiration: Ref<TokenExpirationInfo | null>,
    hasUnpaidOrder: Ref<boolean>,
    onExpired: () => void,
    onTimeUpdate?: (time: number) => void,
  ): void {
    this.stop()
    this.start(tokenExpiration, hasUnpaidOrder, onExpired, onTimeUpdate)
  }
}

/**
 * Создает и возвращает новый экземпляр таймера истечения
 * @returns экземпляр ExpirationTimer
 */
export function createExpirationTimer(): ExpirationTimer {
  return new ExpirationTimer()
}

/**
 * Утилитарная функция для создания простого таймера
 * @param callback - функция для выполнения
 * @param intervalMs - интервал в миллисекундах
 * @returns функция для остановки таймера
 */
export function createSimpleTimer(callback: () => void, intervalMs: number): () => void {
  const timer = setInterval(callback, intervalMs)
  
  return () => {
    clearInterval(timer)
  }
}

/**
 * Создает таймер с задержкой
 * @param callback - функция для выполнения
 * @param delayMs - задержка в миллисекундах
 * @returns функция для отмены таймера
 */
export function createDelayedTimer(callback: () => void, delayMs: number): () => void {
  const timer = setTimeout(callback, delayMs)
  
  return () => {
    clearTimeout(timer)
  }
}